import React from 'react';
import { Flashbar } from 'AWS-UI-Components-React';
import {
  ERROR_MESSAGE,
} from '../../../constants/views';
import { extractErrorMessage } from '../../../utilities.js';

import { LOCAL_STORAGE_KEYS, saveToLocalStorage } from '../../../utils/local_storage';
import { accumulateProductLineById } from '../../../utils/product_line';

import { getJSClient } from '../../js_client';

import { PLGLMappingTable } from './pl_gl_mapping_table';

export class PLGLMapperContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      productLinesById: {},
      productGroupsById: {},
      rootProductLine: {},
      errorScreenMessage: null,
    };
  }

  componentWillMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.refreshPlHierarchy();
    this.refreshGLProductGroups();
  };

  refreshPlHierarchy = (id = 'ALL', version, successCallback, errorCallback) => {
    this.client.getPlHierarchy(
      successCallback || this.onPlHierarchyPullSuccess,
      errorCallback || this.onPlHierarchyPullFailure,
      {
        version: version || this.props.user,
        id,
      },
    );
  };

  onPlHierarchyPullSuccess = (data) => {
    const productLinesById = {};
    accumulateProductLineById(data.root, null, productLinesById);
    this.setState({ productLinesById, rootProductLine: data.root, errorScreenMessage: null });
  };

  onPlHierarchyPullFailure = (err) => {
    console.error(err);
    this.setState({ errorScreenMessage: extractErrorMessage(err) });
  };

  refreshGLProductGroups = () => {
      this.client.getGLProductGroups(this.onGLProductGroupPullSuccess, this.onGLProductGroupPullFailure);
  };

  populateProductGroups = (productGroups) => {
    const productGroupsById = {};
    for (const productGroup of productGroups) {
      productGroupsById[productGroup.id] = productGroup;
    }

    this.setState({ productGroupsById });
  };

  onGLProductGroupPullSuccess = (data) => {
    this.populateProductGroups(data.glProductGroupList);
    saveToLocalStorage(LOCAL_STORAGE_KEYS.GL_PRODUCT_GROUPS, data.glProductGroupList);
    this.setState({ errorScreenMessage: null });
  };

  onGLProductGroupPullFailure = (err) => {
    console.error(err);
    this.setState({ errorScreenMessage: extractErrorMessage(err) });
  };

  stageProductLineChanges = (modifiedProductLinesById) => {
    // Optimistic update - we update the product line in the view
    // before making an API call to stage it.
    const productLinesById = { ...this.state.productLinesById };
    for (const id of Object.keys(modifiedProductLinesById)) {
      const productLine = modifiedProductLinesById[id];
      productLine.parentItem = productLinesById[productLine.parent];
      productLine.isStaged = true;
      productLinesById[productLine.id] = productLine;
    }

    this.setState({ productLinesById });

    const payload = this.convertToStageRequestPayload(modifiedProductLinesById);
    this.client.stageProductLine(this.onStageProductLineSuccess, this.onStageProductLineFailure, payload);
  };

  convertToStageRequestPayload = (modifiedProductLinesById) => {
    const payload = [];
    for (const productLineId of Object.keys(modifiedProductLinesById)) {
      payload.push({
        ...modifiedProductLinesById[productLineId],
        // Stage API accepts only parent Ids - not the parent item object.
        // So remove the field 'parentItem` and populate the field
        // 'parent' with the Id of the parent item.
        parent: modifiedProductLinesById[productLineId].parentItem.id,
        parentItem: undefined,
      });
    }

    return { productLines: payload };
  };

  onStageProductLineSuccess = (data) => {
    console.log('Staging productline succeeded.');
    this.setState({ errorScreenMessage: null });
  };

  onStageProductLineFailure = (error) => {
    console.error(error);
    this.setState({ errorScreenMessage: extractErrorMessage(error) });
  };

  unstageProductLineChanges = (stagedProductLineIds, latestProductLinesById) => {
    // Optimistic update - we update the product line in the view
    // before making an API call to unstage it.
    const productLinesById = { ...this.state.productLinesById };
    for (const id of stagedProductLineIds) {
      productLinesById[id] = { ...latestProductLinesById[id], isStaged: false };
    }

    this.setState({ productLinesById });

    const payload = this.convertToUnstageRequestPayload(stagedProductLineIds);
    this.client.unstageProductLine(this.onUnstageProductLineSuccess, this.onUnstageProductLineFailure, payload);
  };

  convertToUnstageRequestPayload = (stagedProductLineIds, version) => ({
    productLines: stagedProductLineIds.map(productLineId => ({
      id: productLineId,
      version: version || this.props.user,
    })),
  });

  onUnstageProductLineSuccess = (data) => {
    console.log('Unstaging productline succeeded.');
    this.setState({ errorScreenMessage: null });
  };

  onUnstageProductLineFailure = (error) => {
    console.error(error);
    this.setState({ errorScreenMessage: extractErrorMessage(error) });
  };

  publishProductLineChanges = (stagedProductLineIds, latestProductLinesById, stagedProductLines) => {
    // Optimistic update - we update the product line in the view
    // before making an API call to publish it.
    let productLinesById = { ...this.state.productLinesById };
    for (let id of stagedProductLineIds) {
      const stagedProductLine = stagedProductLines.find((item) => item.id === id);
      productLinesById[id] = {
        ...stagedProductLine,
        isStaged: false,
        last: stagedProductLine.last ? stagedProductLine.last + 1 : null,
      };
    }

    this.setState({ productLinesById });

    const payload = this.convertToPublishRequestPayload(stagedProductLineIds);
    this.client.publishProductLineMappings(this.onPublishProductLineSuccess, this.onPublishProductLineFailure, payload);
  };

  convertToPublishRequestPayload = (stagedProductLineIds, version) => {
    return {
      productLines: stagedProductLineIds.map((productLineId) => {
        return {
          id: productLineId,
          version: version || this.props.user,
        };
      }),
    };
  };

  onPublishProductLineSuccess = (data) => {
    console.log('Publishing productline succeeded.');
    this.setState({ errorScreenMessage: null });
  };

  onPublishProductLineFailure = (error) => {
    console.error(error);
    this.setState({ errorScreenMessage: extractErrorMessage(error) });
  };

  render() {
    const { productLinesById, productGroupsById, errorScreenMessage } = this.state;

    return (
      <div>
        <div>
          <Flashbar
            items={
              errorScreenMessage
                ? [
                    {
                      type: 'error',
                      dismissible: true,
                      content: `${ERROR_MESSAGE}${errorScreenMessage}`,
                      dismiss: () => this.setState({ errorScreenMessage: null }),
                    },
                  ]
                : []
            }
          />
        </div>
        <div className="awsui-util-container">
          <PLGLMappingTable
            productLinesById={productLinesById}
            productGroupsById={productGroupsById}
            stageProductLineChanges={this.stageProductLineChanges}
            unstageProductLineChanges={this.unstageProductLineChanges}
            publishProductLineChanges={this.publishProductLineChanges}
            refreshPlHierarchy={this.refreshPlHierarchy}
            errorScreenMessage={errorScreenMessage}
            {...this.props}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/pl_gl_mapping/container.jsx